import { Button } from "@package/subframe/components/Button";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Spinner } from "@/components/ui/spinner";
import {
  useListNurseryEventTypeConfigsQueryControllerList,
  useUpdateNurseryEventTypeConfigCommandControllerUpdate,
} from "@/generated/event-type-configs/event-type-configs";
import type { EventLifecycleRole } from "@/generated/model/eventLifecycleRole";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";
import {
  changedEventTypeIds,
  draftFromConfigs,
  invalidateAcknowledgementRuleQueries,
  isDraftDirty,
  isDraftValid,
  type RulesDraft,
  toggleRole,
} from "@/lib/event-records/acknowledgement-rules";
import { invalidateEventRecordQueries } from "@/lib/event-records/invalidate-event-record-queries";

import { EventTypeRulesCard } from "./EventTypeRulesCard";

interface EventAcknowledgementRulesTabProps {
  nurseryId: string;
  canManage: boolean;
}

/**
 * Per-nursery event acknowledgement rules, for the group admin (NAU-342 AC 4).
 *
 * Sits on the branch page beside every other per-nursery setting. One Save button
 * rather than per-row autosave, so "at least one role per action" can be validated
 * before anything is written — unticking the last role would otherwise race an
 * autosave into an unusable state.
 */
export function EventAcknowledgementRulesTab({
  nurseryId,
  canManage,
}: EventAcknowledgementRulesTabProps) {
  const queryClient = useQueryClient();
  const updateConfig = useUpdateNurseryEventTypeConfigCommandControllerUpdate();

  const { data, isLoading, isError } =
    useListNurseryEventTypeConfigsQueryControllerList(nurseryId, {
      query: { enabled: !!nurseryId },
    });

  const configs = data?.data?.data ?? [];

  // Seeded during render when the response identity changes, rather than in an
  // effect: an effect would paint an empty matrix first. Same guard the Events Hub
  // uses for its paging reset.
  const [draft, setDraft] = useState<RulesDraft>({});
  const [seededFrom, setSeededFrom] = useState<typeof configs | undefined>(
    undefined,
  );
  if (configs.length > 0 && seededFrom !== configs) {
    setSeededFrom(configs);
    setDraft(draftFromConfigs(configs));
  }

  const dirty = isDraftDirty(configs, draft);
  const valid = isDraftValid(draft);

  const toggleAcknowledger = (
    eventTypeId: string,
    role: EventLifecycleRole,
  ) => {
    setDraft((previous) => {
      const row = previous[eventTypeId] ?? {
        acknowledgerRoles: [],
        resolverRoles: [],
      };
      return {
        ...previous,
        [eventTypeId]: {
          ...row,
          acknowledgerRoles: toggleRole(row.acknowledgerRoles, role),
        },
      };
    });
  };

  const toggleResolver = (eventTypeId: string, role: EventLifecycleRole) => {
    setDraft((previous) => {
      const row = previous[eventTypeId] ?? {
        acknowledgerRoles: [],
        resolverRoles: [],
      };
      return {
        ...previous,
        [eventTypeId]: {
          ...row,
          resolverRoles: toggleRole(row.resolverRoles, role),
        },
      };
    });
  };

  const discard = () => setDraft(draftFromConfigs(configs));

  const save = async () => {
    const changed = changedEventTypeIds(configs, draft);
    let saved = 0;
    try {
      // One request per changed event type: the API keys rules by (nursery, type),
      // and sending only what moved keeps an untouched row's audit stamp intact.
      for (const eventTypeId of changed) {
        const row = draft[eventTypeId];
        if (!row) {
          continue;
        }
        await updateConfig.mutateAsync({
          nurseryId,
          eventTypeId,
          data: {
            acknowledgerRoles: row.acknowledgerRoles,
            resolverRoles: row.resolverRoles,
          },
        });
        saved += 1;
      }
      toast.success(
        changed.length === 1
          ? "Acknowledgement rules saved"
          : `Acknowledgement rules saved for ${changed.length} event types`,
      );
    } catch (error) {
      toast.error(
        extractApiErrorMessage(error, "Failed to save acknowledgement rules"),
      );
    } finally {
      // Refresh even when a row failed part-way: some rows committed, so leaving the
      // cache alone would let "Discard changes" reset the form to stale values and
      // hide the writes that did land.
      if (saved > 0) {
        await Promise.all([
          invalidateAcknowledgementRuleQueries(queryClient, nurseryId),
          invalidateEventRecordQueries(queryClient),
        ]);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <span className="text-body font-body text-error-600">
        Could not load acknowledgement rules. Please try again.
      </span>
    );
  }

  if (configs.length === 0) {
    return (
      <span className="text-body font-body text-subtext-color">
        No event types are available yet. Event types are seeded platform-wide.
      </span>
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full flex-col items-start gap-1">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Event acknowledgement
        </span>
        <span className="text-body font-body text-subtext-color">
          For each kind of event, choose who must acknowledge it and who can
          mark it resolved. These rules apply to this nursery only.
        </span>
      </div>

      {configs.map((config) => (
        <EventTypeRulesCard
          key={config.eventTypeId}
          config={config}
          draft={
            draft[config.eventTypeId] ?? {
              acknowledgerRoles: config.acknowledgerRoles,
              resolverRoles: config.resolverRoles,
            }
          }
          disabled={!canManage || updateConfig.isPending}
          onToggleAcknowledger={(role) =>
            toggleAcknowledger(config.eventTypeId, role)
          }
          onToggleResolver={(role) => toggleResolver(config.eventTypeId, role)}
        />
      ))}

      {canManage ? (
        <div className="flex w-full items-center justify-end gap-2">
          {dirty && (
            <Button
              variant="neutral-secondary"
              onClick={discard}
              disabled={updateConfig.isPending}
            >
              Discard changes
            </Button>
          )}
          <Button
            variant="brand-primary"
            onClick={save}
            disabled={!dirty || !valid || updateConfig.isPending}
            loading={updateConfig.isPending}
          >
            Save changes
          </Button>
        </div>
      ) : (
        <span className="text-body font-body italic text-subtext-color">
          Contact your nursery group admin to change these rules.
        </span>
      )}
    </div>
  );
}
